import { ColDef } from '@ag-grid-community/core';
import { dateComparator, dateFormatter } from '@store/loadboard/utils';
import { formatAmount } from '@utils/utils';
import { DateTime } from 'luxon';

const filterParams = {
  filterOptions: [
    'startsWith',
    'equals',
    'contains',
    'notEqual',
    'endsWith',
    'notContains',
  ],
  buttons: ['apply', 'reset'],
  suppressAndOrCondition: true,
};

export const efsHistoryColumns: ColDef[] = [
  {
    headerName: 'Load Number',
    field: 'loadNum',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: filterParams,
    minWidth: 100,
  },
  {
    headerName: 'Issued Date',
    field: 'issuedDate',
    filter: 'agDateColumnFilter',
    sort: 'desc',
    filterParams: {
      suppressAndOrCondition: true,
      filterOptions: ['equals', 'lessThan', 'greaterThan', 'inRange'],
      buttons: ['apply', 'clear'],
      comparator: dateComparator,
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    valueFormatter: dateFormatter,
    floatingFilter: true,
    minWidth: 100,
    maxWidth: 150,
  },
  {
    headerName: 'Issued To',
    field: 'issuedTo',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: filterParams,
    minWidth: 340,
    maxWidth: 360,
  },
  {
    headerName: 'Amount',
    field: 'amount',
    floatingFilter: true,
    filter: 'agNumberColumnFilter',
    filterParams: {
      suppressAndOrCondition: true,
      buttons: ['apply', 'reset'],
      filterOptions: [
        'equals',
        'lessThan',
        'greaterThan',
        'lessThanOrEqual',
        'greaterThanOrEqual',
      ],
    },
    valueFormatter: (params) => {
      const value = params.value as number | undefined | null;
      if (value !== undefined && value !== null) {
        return formatAmount(value);
      }
      return '';
    },
    minWidth: 100,
  },
  {
    headerName: 'CheckNumber',
    field: 'checkNumber',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: filterParams,
    minWidth: 100,
  },
  {
    headerName: 'Ref #',
    field: 'referenceNumber',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: filterParams,
    minWidth: 100,
  },
  {
    headerName: 'Description',
    field: 'description',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: filterParams,
    minWidth: 250,
    maxWidth: 280,
  },
  {
    headerName: 'RequestedBy',
    field: 'requestedBy',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: filterParams,
    minWidth: 100,
  },
  {
    headerName: 'ApprovedBy',
    field: 'approvedBy',
    filter: 'agTextColumnFilter',
    floatingFilter: true,
    filterParams: filterParams,
    minWidth: 100,
  },
];

export const filterTypeMapping = {
  equals: 'EQUALS',
  notEqual: 'NOT_EQUALS',
  greaterThan: 'GREATER_THAN',
  greaterThanOrEqual: 'GREATER_THAN_OR_EQUALS',
  lessThan: 'LESS_THAN',
  lessThanOrEqual: 'LESS_THAN_OR_EQUALS',
  contains: 'CONTAINS',
  notContains: 'NOT_CONTAINS',
  startsWith: 'STARTS_WITH',
  endsWith: 'ENDS_WITH',
  blank: 'BLANK',
  notBlank: 'NOT_BLANK',
  inRange: 'IN_RANGE',
  set: 'EQUALS',
};

export const transformFilters = (inputFilters: { [key: string]: unknown }) => {
  const outputFilters = Object.keys(inputFilters).map((key) => {
    const filter = inputFilters[key] as {
      filterType: string;
      type: keyof typeof filterTypeMapping;
      filter: string;
      dateFrom?: string | null;
      dateTo?: string | null;
      values?: string[];
    };
    if ((filter as { filterType: string }).filterType === 'date') {
      const dateFilters = [];

      if (Boolean(filter.dateFrom)) {
        dateFilters.push(
          DateTime.fromFormat(
            filter.dateFrom as string,
            'yyyy-MM-dd HH:mm:ss',
          ).toFormat('MM/dd/yyyy'),
        );
      }

      if (Boolean(filter.dateTo)) {
        dateFilters.push(
          DateTime.fromFormat(
            filter.dateTo as string,
            'yyyy-MM-dd HH:mm:ss',
          ).toFormat('MM/dd/yyyy'),
        );
      }
      return {
        field: key,
        filterType:
          filterTypeMapping[
            (filter as { type: keyof typeof filterTypeMapping }).type
          ],
        values: dateFilters,
      };
    }
    if ((filter as { filterType: string }).filterType === 'set') {
      return (
        (filter.values ?? []).length > 0 && {
          field: key,
          filterType: 'IN',
          values: filter.values,
        }
      );
    }
    if ((filter as { filterType: string }).filterType === 'text') {
      return {
        field: key,
        filterType:
          filterTypeMapping[
            (filter as { type: keyof typeof filterTypeMapping }).type
          ],
        values: [(filter as { filter: string }).filter],
      };
    }
    if ((filter as { filterType: string }).filterType === 'number') {
      return {
        field: key,
        filterType:
          filterTypeMapping[
            (filter as { type: keyof typeof filterTypeMapping }).type
          ],
        values: [(filter as { filter: string }).filter],
      };
    }
  });

  return outputFilters.filter(Boolean);
};

const parseCsvLine = (line: string): string[] => {
  const regex = /("([^"]|"")*"|[^,]*)(,|$)/g;
  const result: string[] = [];
  let match;
  while ((match = regex.exec(line)) !== null) {
    let cell = match[1].trim();
    if (cell.startsWith('"') && cell.endsWith('"')) {
      cell = cell.slice(1, -1).replace(/""/g, '"');
    }
    result.push(cell);
    if (regex.lastIndex === match.index) regex.lastIndex++; // avoid infinite loop
  }
  return result;
};

export const downloadCsv = (
  csvData: string,
  filename = 'Export.csv',
  removeFirstColumn = false,
) => {
  const cleanedCsv = csvData
    .split('\n')
    .filter(Boolean)
    .map((line: string) => {
      const columns = parseCsvLine(line);
      const finalColumns = removeFirstColumn ? columns.slice(1) : columns;

      return finalColumns
        .map((col) => {
          if (col.includes(',') || col.includes('"')) {
            return `"${col.replace(/"/g, '""')}"`;
          }
          return col;
        })
        .join(',');
    })
    .join('\n');

  const blob = new Blob([cleanedCsv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
